import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Globe, Plus, Check, X, Languages, Copy, ChevronDown, ChevronUp
} from 'lucide-react';

import { API_BASE_URL, getAuthHeaders as getCoreAuthHeaders } from '../../api/core.js';

function getAuthHeaders() {
  return { 'Content-Type': 'application/json', ...getCoreAuthHeaders() };
}

// Available languages
const LANGUAGES = [
  { code: 'fr', name: 'Francais', flag: '🇫🇷' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'es', name: 'Espanol', flag: '🇪🇸' }
];

// API functions with authentication
const variantsAPI = {
  getVariants: async (templateId) => {
    const res = await fetch(`${API_BASE_URL}/email_templates/${templateId}/variants`, {
      headers: getAuthHeaders()
    });
    return res.json();
  },
  createVariant: async (templateId, data) => {
    const res = await fetch(`${API_BASE_URL}/email_templates/${templateId}/translate`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return res.json();
  }
};

function LanguageVariantsPanel({ template, onMessage, onSelectVariant }) {
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [showTranslateModal, setShowTranslateModal] = useState(false);

  // Fetch variants for this template
  const { data: variantsData, isLoading } = useQuery({
    queryKey: ['templateVariants', template.id],
    queryFn: () => variantsAPI.getVariants(template.id),
    enabled: expanded
  });

  const variants = variantsData?.data || [];

  // Find which languages already have variants
  const existingLanguages = variants.map(v => v.language);
  const availableLanguages = LANGUAGES.filter(l => !existingLanguages.includes(l.code));

  // Create variant mutation
  const createVariantMutation = useMutation({
    mutationFn: (data) => variantsAPI.createVariant(template.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['templateVariants', template.id]);
      queryClient.invalidateQueries(['emailTemplates']);
      setShowTranslateModal(false);
      onMessage?.({ title: 'Succes', message: 'Variante de langue creee', type: 'success' });
    },
    onError: (err) => {
      onMessage?.({ title: 'Erreur', message: err.message, type: 'error' });
    }
  });

  if (!expanded) {
    return (
      <button
        onClick={() => setExpanded(true)}
        className="flex items-center gap-2 text-sm text-gray-500 hover:text-blue-600"
      >
        <Globe className="w-4 h-4" />
        <span>{LANGUAGES.find(l => l.code === template.language)?.flag} {template.language.toUpperCase()}</span>
        <ChevronDown className="w-3 h-3" />
      </button>
    );
  }

  return (
    <div className="bg-gray-50 rounded-lg p-3 mt-2">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <button
          onClick={() => setExpanded(false)}
          className="flex items-center gap-2 text-sm font-medium"
        >
          <Languages className="w-4 h-4" />
          Variantes de langue
          <ChevronUp className="w-3 h-3" />
        </button>
        {availableLanguages.length > 0 && (
          <button
            onClick={() => setShowTranslateModal(true)}
            className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"
          >
            <Plus className="w-3 h-3" />
            Traduire
          </button>
        )}
      </div>

      {/* Variants list */}
      {isLoading ? (
        <div className="text-sm text-gray-500">Chargement...</div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {variants.map(variant => (
            <button
              key={variant.id}
              onClick={() => onSelectVariant?.(variant)}
              className={`flex items-center gap-1 px-2 py-1 rounded text-sm ${
                variant.id === template.id
                  ? 'bg-blue-100 text-blue-700 border border-blue-200'
                  : 'bg-white text-gray-600 border hover:border-blue-200'
              }`}
            >
              {LANGUAGES.find(l => l.code === variant.language)?.flag}
              <span>{variant.language.toUpperCase()}</span>
              {variant.id === template.id && <Check className="w-3 h-3" />}
            </button>
          ))}
        </div>
      )}

      {/* Translate Modal */}
      {showTranslateModal && (
        <TranslateModal
          template={template}
          availableLanguages={availableLanguages}
          onClose={() => setShowTranslateModal(false)}
          onSave={(data) => createVariantMutation.mutate(data)}
          isLoading={createVariantMutation.isPending}
        />
      )}
    </div>
  );
}

// Translation Modal
function TranslateModal({ template, availableLanguages, onClose, onSave, isLoading }) {
  const [selectedLanguage, setSelectedLanguage] = useState(availableLanguages[0]?.code || '');
  const [formData, setFormData] = useState({
    template_name: template.template_name,
    subject: template.subject,
    body_text: template.body_text,
    body_html: template.body_html || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      language: selectedLanguage,
      ...formData
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl m-4 max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b flex items-center justify-between shrink-0">
          <h3 className="font-semibold flex items-center gap-2">
            <Languages className="w-5 h-5" />
            Creer une variante de langue
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-auto p-6 space-y-4">
          {/* Source info */}
          <div className="bg-blue-50 rounded-lg p-3 text-sm">
            <div className="font-medium text-blue-700">Modele source</div>
            <div className="text-blue-600">
              {LANGUAGES.find(l => l.code === template.language)?.flag} {template.template_name}
            </div>
          </div>

          {/* Target language */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Langue cible</label>
            <div className="flex gap-2">
              {availableLanguages.map(lang => (
                <button
                  key={lang.code}
                  type="button"
                  onClick={() => setSelectedLanguage(lang.code)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${
                    selectedLanguage === lang.code
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <span className="text-lg">{lang.flag}</span>
                  <span>{lang.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Template Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nom du modele ({selectedLanguage.toUpperCase()})
            </label>
            <input
              type="text"
              value={formData.template_name}
              onChange={(e) => setFormData({ ...formData, template_name: e.target.value })}
              className="w-full border rounded-lg px-3 py-2"
              required
            />
          </div>

          {/* Subject */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Objet</label>
            <div className="relative">
              <input
                type="text"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 pr-10"
                required
              />
              <button
                type="button"
                onClick={() => setFormData({ ...formData, subject: template.subject })}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                title="Copier depuis l'original"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Original: {template.subject}
            </div>
          </div>

          {/* Body Text */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Corps du message</label>
            <div className="relative">
              <textarea
                value={formData.body_text}
                onChange={(e) => setFormData({ ...formData, body_text: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 h-48 font-mono text-sm"
                required
              />
              <button
                type="button"
                onClick={() => setFormData({ ...formData, body_text: template.body_text })}
                className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
                title="Copier depuis l'original"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* HTML Body (optional) */}
          {template.body_html && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Corps HTML (optionnel)
              </label>
              <textarea
                value={formData.body_html}
                onChange={(e) => setFormData({ ...formData, body_html: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 h-32 font-mono text-sm"
              />
            </div>
          )}
        </form>

        {/* Actions */}
        <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:bg-gray-200 rounded-lg"
          >
            Annuler
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading || !selectedLanguage}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {isLoading ? 'Creation...' : 'Creer la variante'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default LanguageVariantsPanel;
